import { CommonModule } from '@angular/common';
import {
  afterNextRender,
  ChangeDetectorRef,
  Component,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  signal,
  Signal,
  SimpleChanges,
  untracked,
  ViewChild,
  ViewChildren,
  WritableSignal
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoadingPopupComponent } from 'projects/ui-library/src/lib/components/loading/loading-popup/loading-popup.component';
import { Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { PDFExporter, PdfGrid } from 'src/app/classes/shared/pdfExporter';
import { Pop } from 'src/app/classes/shared/pop';
import { UiTool } from 'src/app/classes/shared/ui.tools';
import {
  bottomOf, cellAt, columnsFor, deriveLayout, fitRowHeight, GRID_COLUMNS, GRID_ROW_HEIGHT, GridItem, moveItem, pitchOf, resizeItem, resolveLayout,
  sameLayout, spanOf, toPixels
} from 'src/app/classes/widget/dashboard-grid';
import { FullscreenWidgetComponent } from 'src/app/components/project-pages/dashboard/dialogs/fullscreen-widget/fullscreen-widget.component';
import { WidgetCatalogComponent } from 'src/app/components/project-pages/dashboard/dialogs/widget-catalog/widget-catalog.component';
import { WidgetEditorComponent } from 'src/app/components/project-pages/dashboard/dialogs/widget-editor/widget-editor.component';
import { WidgetV2 } from 'src/app/interfaces/api/chart';
import { CompanyDashboard, WidgetEditorScope } from 'src/app/interfaces/api/dashboard';
import { User } from 'src/app/interfaces/api/user';
import defaultDashboard, { Dashboard } from 'src/app/interfaces/shared/dashboard';
import { ChartService } from 'src/app/services/api/charts/chart.service';
import { DashboardService } from 'src/app/services/api/dashboards/dashboard.service';
import { AuthService } from 'src/app/services/core/auth.service';
import { DashboardStore } from 'src/app/services/stores/dashboard.store';
import { ButtonComponent } from 'ui-library';
import { ChartComponent } from '../widgets/chart/chart.component';
import { WidgetHostComponent } from '../widgets/widget-host/widget-host.component';

// A widget on the grid: CSS grid-area and size in px.
interface GridCell {
  area: string;
  width: number;
  height: number;
}

// Until the grid is measured (hidden panel, tests).
const FALLBACK_GRID_WIDTH = 1200;

// Auto-scroll: band near the edges (px), speed (px per frame).
const SCROLL_EDGE = 60;
const SCROLL_STEP = 14;

export type GridHandle = 'move' | 'e' | 's' | 'se';

// A move or resize in progress.
interface GridGesture {
  id: string;
  handle: GridHandle;
  element: HTMLElement;
  origin: GridItem[];          // layout at start
  start: GridItem;
  grab: { x: number; y: number };   // pointer offset in the widget (move) or start pointer (resize)
  pointer: { x: number; y: number };
  preview: GridItem[];
}

@Component({
  selector: 'app-dashboard-panel',
  imports: [
    CommonModule,
    MatIconModule,
    MatMenuModule,
    WidgetHostComponent,
    LoadingPopupComponent,
    ButtonComponent,
    TranslateModule
  ],
  templateUrl: './dashboard-panel.component.html',
  standalone: true,
  styleUrls: ['./dashboard-panel.component.scss']
})

export class DashboardPanelComponent implements OnChanges, OnDestroy {
  // Services
  private readonly authService: AuthService = inject(AuthService);
  private readonly chartService: ChartService = inject(ChartService);
  public readonly uiTool: UiTool = inject(UiTool);
  private readonly dialog: MatDialog = inject(MatDialog);
  private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  private readonly zone: NgZone = inject(NgZone);
  // Catalog / editor dialogs open over this dashboard (see pauseUnderEditor).
  private editorDialogs: number = 0;
  private readonly pop: Pop = inject(Pop);
  private readonly translate: TranslateService = inject(TranslateService);
  private readonly dashboardStore: DashboardStore = inject(DashboardStore);
  // Only for a synthesis (company dashboard routes): resolved when needed, the project path never builds it.
  private readonly injector: Injector = inject(Injector);

  // Component API
  @ViewChild('grid', { static: true }) gridRef!: ElementRef<HTMLElement>;
  @ViewChildren(WidgetHostComponent) widgetHosts!: QueryList<WidgetHostComponent>;

  @Input() dashboard: Dashboard = defaultDashboard;
  @Input() filters: any;
  @Input('charts') items: WidgetV2[] = [];
  @Input() projects: any[] = [];
  @Input() selectedProjects: string[] = [];
  @Input() theme: string = 'light';
  @Input() project: any;
  @Input() units: any[] | undefined;
  @Input() refreshSignal?: WritableSignal<number>;
  @Input() exportSignal?: WritableSignal<string>;
  // Synthesis: the company dashboard of a theme, its widgets on the open tenant. Absent: the project dashboard, unchanged.
  @Input() scope: WidgetEditorScope | null = null;
  // Synthesis: the tenant projects removed from the page.
  @Input() excludeProjects?: Signal<string[]>;
  @Input() canEdit: boolean = false;
  // Big screens: rows stretched so the whole dashboard fills the height.
  @Input() fit: boolean = false;
  @Output() companyDashboardChange: EventEmitter<CompanyDashboard> = new EventEmitter();

  // Variables
  columns: number = GRID_COLUMNS;
  rowHeight: number = GRID_ROW_HEIGHT;
  // All placements (hidden widgets kept); visible widgets in reading order and their cells.
  layout: GridItem[] = [];
  gridItems: WidgetV2[] = [];
  cells: Record<string, GridCell> = {};
  private placed: GridItem[] = [];
  private gridWidth: number = 0;
  private gridHeight: number = 0;
  private resizeObserver?: ResizeObserver;
  gesture: GridGesture | null = null;
  // Target cell of the gesture.
  ghost: string | null = null;
  // The widget under the mouse, and the one whose "more" menu is open (its buttons stay shown).
  pointerItem: WidgetV2 | null = null;
  menuItem: WidgetV2 | null = null;

  private receivedCanvas: Record<string, HTMLCanvasElement | null> = {};
  private pdfExported: boolean = false;

  backgroundColor: string = "#eee";
  borderColor: string = "#bbb";
  exportSub: Subscription | undefined;
  projectSettings: any;
  trackers: any[] | undefined;
  timeout: string | number | NodeJS.Timeout | undefined;
  destroy$ = new Subject<void>();
  exportedCharts: Record<string, boolean> = {};
  exporting: boolean = false;
  widgetsCount: number = 0;
  currentStage: number = 0;
  refreshInterval: any;

  // Signals
  signalSingled: WritableSignal<string> = signal('');
  readonly user: Signal<User> = this.authService.currentUser;
  readonly canManageProject: Signal<boolean> = this.authService.canManageProject;

  constructor() {
    let lastExportValue: string | undefined = undefined;

    effect(() => {
      if (this.exportSignal) {
        const value = this.exportSignal();

        if (lastExportValue !== undefined && value !== lastExportValue) {
          if (value.startsWith('canvas'))
            this.export();
        }

        lastExportValue = value;
      }
    });

    this.pauseUnderEditor();
    afterNextRender(() => this.observeGrid());
  }

  // Grid size, then its changes (debounced: heavy widgets redraw once).
  private observeGrid(): void {
    const grid = this.gridRef.nativeElement;
    const container = grid.parentElement!;
    this.setGridSize(grid.clientWidth || FALLBACK_GRID_WIDTH, container.clientHeight);

    this.resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.timeout);
      this.timeout = setTimeout(() => this.setGridSize(grid.clientWidth, container.clientHeight), 150);
    });
    this.resizeObserver.observe(container);
  }

  // Width 0: hidden panel, unchanged.
  private setGridSize(width: number, height: number): void {
    if (!width || (width === this.gridWidth && height === this.gridHeight)) return;

    this.gridWidth = width;
    this.gridHeight = height;
    this.columns = columnsFor(width);
    this.placeWidgets();
    this.cdr.detectChanges();
  }

  // Saved placement, else from the former index.
  private refreshLayout(): void {
    const widgets = this.items.filter(item => item.id).map(item => ({ id: item.id!, index: item.index, width: item.width, height: item.height }));
    this.layout = resolveLayout(this.dashboard.layout?.items, widgets);
    this.placeWidgets();
  }

  // Visible widgets on the current columns, compacted.
  private placeWidgets(): void {
    const visible = new Map(this.items.filter(item => item.visible && item.id).map(item => [item.id!, item]));
    const width = this.gridWidth || FALLBACK_GRID_WIDTH;

    this.placed = deriveLayout(this.layout.filter(item => visible.has(item.id)), this.columns);
    this.rowHeight = this.fitted() ? fitRowHeight(bottomOf(this.placed), this.gridHeight) : GRID_ROW_HEIGHT;
    this.gridItems = this.placed.map(item => visible.get(item.id)!);
    this.cells = Object.fromEntries(this.placed.map(item => [item.id, {
      area: this.areaOf(item),
      ...toPixels(item, width, this.columns, this.rowHeight),
    }]));
  }

  private areaOf(item: GridItem): string {
    return `${item.y + 1} / ${item.x + 1} / span ${item.h} / span ${item.w}`;
  }

  // Fitted: desktop grid, height known.
  fitted(): boolean {
    return this.fit && this.columns === GRID_COLUMNS && this.gridHeight > 0;
  }

  // Moving and resizing: desktop grid, not fitted.
  canArrange(): boolean {
    return this.columns === GRID_COLUMNS && !this.fitted() && this.canEditWidgets();
  }

  // Pointer down on a handle: the gesture runs outside Angular, the layout changes only when the target cell does.
  startGesture(event: PointerEvent, item: WidgetV2, handle: GridHandle): void {
    const start = this.placed.find(placed => placed.id === item.id);
    const element = (event.target as HTMLElement).closest<HTMLElement>('.drag-box');
    if (!start || !element || event.button !== 0 || this.gesture) return;

    event.preventDefault();
    const box = element.getBoundingClientRect();
    this.gesture = {
      id: start.id, handle, element, start, origin: this.placed, preview: this.placed,
      grab: handle === 'move' ? { x: event.clientX - box.left, y: event.clientY - box.top } : { x: event.clientX, y: event.clientY },
      pointer: { x: event.clientX, y: event.clientY },
    };
    this.ghost = this.areaOf(start);

    this.zone.runOutsideAngular(() => {
      document.addEventListener('pointermove', this.onPointerMove);
      document.addEventListener('pointerup', this.onPointerUp);
      document.addEventListener('pointercancel', this.onPointerCancel);
      requestAnimationFrame(this.autoScroll);
    });
  }

  private readonly onPointerMove = (event: PointerEvent): void => {
    if (!this.gesture) return;
    this.gesture.pointer = { x: event.clientX, y: event.clientY };
    this.track();
  };

  private readonly onPointerUp = (): void => this.zone.run(() => this.endGesture(true));
  private readonly onPointerCancel = (): void => this.zone.run(() => this.endGesture(false));

  // Follows the pointer: the widget by transform, the others by grid-area when the target changes.
  private track(): void {
    const gesture = this.gesture;
    if (!gesture) return;

    const grid = this.gridRef.nativeElement.getBoundingClientRect();
    const pitch = pitchOf(grid.width);
    const { start, pointer, grab } = gesture;
    let preview: GridItem[];

    if (gesture.handle === 'move') {
      const left = pointer.x - grid.left - grab.x;
      const top = pointer.y - grid.top - grab.y;
      gesture.element.style.transform = `translate(${left - start.x * pitch.x}px, ${top - start.y * pitch.y}px)`;

      const cell = cellAt(left, top, grid.width);
      preview = moveItem(gesture.origin, start.id, cell.x, cell.y);
    } else {
      const size = toPixels(start, grid.width);
      const span = spanOf(
        size.width + (gesture.handle === 's' ? 0 : pointer.x - grab.x),
        size.height + (gesture.handle === 'e' ? 0 : pointer.y - grab.y),
        grid.width,
      );
      preview = resizeItem(gesture.origin, start.id, span.w, span.h);
    }

    if (sameLayout(preview, gesture.preview)) return;

    gesture.preview = preview;
    // Outside Angular like the gesture: the panel's own check is enough for a preview.
    this.showPreview(preview);
  }

  // Others at their new place; the active widget keeps its size until the drop (heavy widgets redraw once).
  private showPreview(preview: GridItem[]): void {
    const active = this.gesture!.id;

    for (const item of preview) {
      if (item.id === active) this.ghost = this.areaOf(item);
      else if (this.cells[item.id]) this.cells[item.id]!.area = this.areaOf(item);
    }
    this.cdr.detectChanges();
  }

  // Near the top or bottom edge: scrolls, and the target follows.
  private readonly autoScroll = (): void => {
    const gesture = this.gesture;
    if (!gesture) return;

    const scroller = this.scrollerOf();
    const view = scroller === this.pageScroller() ? { top: 0, bottom: window.innerHeight } : scroller.getBoundingClientRect();
    let step = 0;
    if (gesture.pointer.y < view.top + SCROLL_EDGE) step = -SCROLL_STEP;
    else if (gesture.pointer.y > view.bottom - SCROLL_EDGE) step = SCROLL_STEP;

    if (step) {
      const before = scroller.scrollTop;
      scroller.scrollTop += step;
      if (scroller.scrollTop !== before) this.track();
    }
    requestAnimationFrame(this.autoScroll);
  };

  // The dashboard's own scroll, else the page's (synthesis).
  private scrollerOf(): Element {
    const container = this.gridRef.nativeElement.parentElement!;
    return container.scrollHeight > container.clientHeight ? container : this.pageScroller();
  }

  private pageScroller(): Element {
    return document.scrollingElement ?? document.documentElement;
  }

  private stopGesture(): GridGesture | null {
    const gesture = this.gesture;
    document.removeEventListener('pointermove', this.onPointerMove);
    document.removeEventListener('pointerup', this.onPointerUp);
    document.removeEventListener('pointercancel', this.onPointerCancel);
    if (gesture) gesture.element.style.transform = '';
    this.gesture = null;
    this.ghost = null;
    return gesture;
  }

  // Drop: new layout kept and saved (hidden widgets unchanged); cancel: back to the start.
  private endGesture(drop: boolean): void {
    const gesture = this.stopGesture();
    if (!gesture) return;

    if (drop && !sameLayout(gesture.preview, gesture.origin)) {
      const moved = new Map(gesture.preview.map(item => [item.id, item]));
      this.saveLayout(this.layout.map(item => moved.get(item.id) ?? item));
    } else {
      this.placeWidgets();
    }
    this.cdr.detectChanges();
  }

  // Shown at once; restored if the save fails.
  private saveLayout(items: GridItem[]): void {
    const previous = this.dashboard.layout;
    const layout = { columns: GRID_COLUMNS, items };
    this.dashboard.layout = layout;
    this.refreshLayout();

    const dashboards = this.injector.get(DashboardService);
    const save = this.scope
      ? dashboards.saveCompanyLayout(this.scope.companyId, this.scope.theme, layout)
      : dashboards.saveLayout(this.dashboard.id, layout);

    save.pipe(takeUntil(this.destroy$)).subscribe({
      error: () => {
        this.dashboard.layout = previous;
        this.refreshLayout();
        this.cdr.markForCheck();
        this.pop.error(this.translate.instant('editor.layout_save_error'));
      }
    });
  }

  // PDF: the grid as displayed.
  private pdfGrid(): PdfGrid {
    // Failed or empty captures left out.
    const items = this.placed
      .map(cell => ({ cell, canvas: this.receivedCanvas[cell.id]! }))
      .filter(item => item.canvas?.width && item.canvas.height);
    return { items, width: this.gridWidth || FALLBACK_GRID_WIDTH, columns: this.columns, rowHeight: this.rowHeight };
  }

  // Under the widget catalog or editor, the dashboard is hidden: it is not checked again on every event of the dialog
  // (the biggest part of each check), then refreshed at once when the last of them closes.
  private pauseUnderEditor(): void {
    this.dialog.afterOpened.pipe(takeUntilDestroyed()).subscribe(ref => {
      if (!(ref.componentInstance instanceof WidgetEditorComponent || ref.componentInstance instanceof WidgetCatalogComponent)) return;

      if (this.editorDialogs++ === 0) this.cdr.detach();
      ref.afterClosed().subscribe(() => {
        if (--this.editorDialogs > 0) return;
        // Checked on the next tick: the editor result is applied by a later afterClosed.
        this.cdr.reattach();
        this.cdr.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    if (this.refreshInterval)
      clearInterval(this.refreshInterval);

    clearTimeout(this.timeout);
    this.resizeObserver?.disconnect();
    this.stopGesture();

    this.destroy$.next();
    this.destroy$.complete();
  }


  ngOnChanges(changes: SimpleChanges) {
    untracked(() => {
      if (!this.dashboard) return;

      this.items = [];

      if (this.dashboard.id != "user") {
        if (this.dashboard.charts && this.dashboard.charts.length > 0) {
          const firstChart = this.dashboard.charts[0];

          if (typeof firstChart === 'object' && firstChart !== null && 'type' in firstChart) {
            this.items = (this.dashboard.charts ?? []) as WidgetV2[];
          }
        } else {
          console.warn('⚠️ No charts in dashboard');
        }
      }

      for (const widget of this.items) {
        if (typeof widget === 'object' && widget !== null && !widget.signalSingled) {
          widget.signalSingled = signal('');
        }
      }

      this.dashboard.charts = this.items;
      this.refreshLayout();
      this.prepareFilter();
    });
  }

  prepareFilter(majFilter: any = false) {
    // Unit / tracker filters belong to a project: none in a synthesis.
    if (this.scope) return;

    if (this.dashboard.projectSettings) {
      this.projectSettings = this.dashboard.projectSettings.find(s => s.project == this.project.id);

      if (majFilter) {
        this.filters = majFilter;
        return;
      }

      this.filters = this.filters.filter((f: { name: string; }) => f.name != 'unitName' && f.name != 'tracker');

      if (this.projectSettings && this.projectSettings.units && this.projectSettings.units.length) {
        if (!this.filters) this.filters = [];

        let unitIndex = this.filters.findIndex((f: { name: string; }) => f.name == 'unitName');
        let trackerIndex = this.filters.findIndex((f: { name: string; }) => f.name == 'tracker');
        let trackers: any[] = [];
        let validUnits: any[] = [];

        if (this.units && this.units.length) {
          this.projectSettings.units.forEach((u: any) => {
            let unit = this.units?.find(_u => _u.name == u)

            if (unit) {
              trackers = trackers.concat(unit.trackers.map((t: { id: any; }) => t.id || t))
              validUnits.push(u);
            }
          })
        }

        this.projectSettings.units = validUnits;
        let filter: any = {}

        if (trackers.length) {
          filter = {
            name: 'tracker',
            type: 'objectid',
            enable: true,
            options: {
              use: 'list',
              values: trackers
            }
          }
        } else if (validUnits.length) {
          filter = {
            name: 'unitName',
            type: 'String',
            enable: true,
            options: {
              use: 'list',
              values: this.projectSettings.units
            }
          }
        }

        if (!filter.enable) return;

        if (unitIndex >= 0 && validUnits.length) {
          this.filters[unitIndex] = filter;
        } else if (trackerIndex >= 0) {
          this.filters[trackerIndex] = filter;
        } else {
          this.filters.push(filter)
        }
      }
    }
  }

  runAutoRefresh() {
    if (!this.dashboard) return;

    let params = this.dashboard.autoRefresh || { value: 1, unit: 'hour' };
    let interval = (params.value ?? 1) * (params.unit == 'hour' ? 3600 : 60);

    if (this.refreshInterval) {
      clearInterval(this.refreshInterval)
    }

    this.refreshInterval = setInterval(() => {
      this.updateAllWidgetData()
    }, interval * 1000)
  }

  updateAllWidgetData() {
    if (this.refreshSignal) {
      this.refreshSignal.set((this.refreshSignal() ?? 0) + 1);
    }
  }

  refreshChart(widget: WidgetV2) {
    if (widget.signalSingled) {
      widget.signalSingled.set(`refresh_manuel_${(new Date()).getTime()}`);
    }
  }

  exportChart(widget: WidgetV2) {
    if (widget.signalSingled) {
      widget.signalSingled.set(`download_${(new Date()).getTime()}`);
    }
  }

  duplicateChart(widget: any) {
    const _widget = {
      ...widget,
      query: widget.query ? JSON.parse(JSON.stringify(widget.query)) : null,
      design: widget.design ? JSON.parse(JSON.stringify(widget.design)) : null,
    };

    delete _widget.id;

    const dialogRef = this.dialog.open(WidgetEditorComponent, {
      width: '95%',
      height: '95%',
      data: {
        widget: _widget,
        dashboard: this.dashboard,
        selectedProjects: this.selectedProjects
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.dashboard) {
        this.updateDashboardState(result.dashboard);
      }
    });
  }

  // Layout handles and the edit / add buttons; in a synthesis, the company dashboard rights.
  canEditWidgets(): boolean {
    if (this.scope) return this.canEdit;
    const user = this.user();
    return !!user && !(user.role.name == 'user' || user.role.name == 'guest');
  }

  canDeleteWidgets(): boolean {
    return this.scope ? this.canEdit : this.canManageProject();
  }

  // The editor works on a copy (nothing to restore on cancel) and hands back the saved widget, a new object.
  editWidget(widget: WidgetV2) {
    const dialogRef = this.dialog.open(WidgetEditorComponent, {
      width: '95%',
      height: '95%',
      data: {
        widget,
        dashboard: this.dashboard,
        selectedProjects: this.selectedProjects,
        scope: this.scope ?? undefined
      }
    });

    dialogRef.afterClosed().subscribe((data) => {
      if (!data) return;

      let { widget: newWidget, dashboard: updatedDashboard } = data;

      let index = this.items.findIndex(c => c.id == newWidget.id)
      if (index >= 0) {
        this.items.splice(index, 1, newWidget);
        this.placeWidgets();
      }

      // The socket echo of the save then merges into it, not into the former object.
      if (!this.scope) this.dashboardStore.replaceWidget(newWidget);

      if (updatedDashboard) {
        this.updateDashboardState(updatedDashboard);
      }
    })
  }

  fullscreen(widget: any) {
    this.dialog.open(FullscreenWidgetComponent, {
      width: '95%',
      height: '95%',
      data: {
        widget,
        user: this.user(),
        projects: this.projects,
        borderColor: this.borderColor,
        filters: this.filters,
        dashboard: this.dashboard,
        selectedProjects: this.selectedProjects
      }
    });
  }

  confirmDeleteChart(widget: WidgetV2) {
    if (!this.canDeleteWidgets()) return;
    if (this.scope) return this.deleteCompanyChart(this.scope, widget);

    this.pop.confirm(
      this.translate.instant('editor.confirm_delete_chart'),
      (confirm: any) => {
        if (confirm.ok) {
          if (typeof widget.id === 'string') {
            this.chartService.removeChart(widget.id)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: () => {
                  let cIndex = this.dashboard.charts?.findIndex(c =>
                    typeof c !== 'string' && c.id === widget.id
                  );

                  if (this.dashboard.charts && cIndex !== undefined && cIndex >= 0) {
                    this.dashboard.charts.splice(cIndex, 1);
                    this.updateDashboardState(this.dashboard);
                  }

                  this.pop.toast(this.translate.instant('editor.successfully_removed_chart'), 'success', 4000, 'delete'
                  );
                },
                error: (error: any) => {
                  if (error == 'offline') {
                    this.pop.warning(this.translate.instant('units.impossible_you_are_offline'));
                  } else {
                    this.pop.error(this.translate.instant('units.error_removing_unit'));
                  }
                }
              });
          }
        }
      }
    );
  }

  onExport(event: HTMLCanvasElement | null, chart: any) {
    chart.canvas = event;
    this.exportedCharts[chart.id] = true;
    this.receivedCanvas[chart.id] = event;

    const totalToExport = this.items.filter(i => i.visible).length;
    const receivedCount = Object.keys(this.receivedCanvas).length;
    this.currentStage = receivedCount;

    if (receivedCount === totalToExport && !this.pdfExported) {
      this.pdfExported = true;

      const dateFilter = this.filters?.find((f: { name: string }) => f.name === 'createdDate');
      const options = {
        title: this.dashboard.title,
        grid: this.pdfGrid(),
        lang: this.user()?.language || 'en',
        projectName: this.project?.name || 'Project',
        startDate: dateFilter?.options?.min,
        endDate: dateFilter?.options?.max,
      };

      const pdf = new PDFExporter({}, options);
      pdf.save();

      this.exportedCharts = {};
      this.receivedCanvas = {};
      this.pdfExported = false;
      this.exporting = false;
      document.querySelector('body')?.classList.remove("overlayed");
    }
  }

  async export() {
    this.exportedCharts = {};
    this.exporting = true;

    if (this.theme === "light") {
      document.querySelector('body')?.classList.add("overlayed");
    }

    this.currentStage = 0;
    this.widgetsCount = this.gridItems.filter(widget => widget.signalSingled).length;

    await new Promise(resolve => setTimeout(resolve, 500));

    // Progress: captures received (onExport).
    for (const chart of this.gridItems) {
      if (!chart.signalSingled) continue;

      this.exportedCharts[chart.id!] = false;
      chart.signalSingled.set("canvas");

      await new Promise(resolve => setTimeout(resolve, 100));
    }
  }

  // Synthesis: out of the company dashboard (which comes back whole), then deleted.
  private deleteCompanyChart(scope: WidgetEditorScope, widget: WidgetV2): void {
    if (typeof widget.id !== 'string') return;
    const chartId = widget.id;

    this.pop.confirm(this.translate.instant('editor.confirm_delete_chart'), (confirm: any) => {
      if (!confirm.ok) return;

      this.injector.get(DashboardService).removeCompanyChart(scope.companyId, scope.theme, chartId).pipe(takeUntil(this.destroy$)).subscribe({
        next: (dashboard: CompanyDashboard) => {
          this.companyDashboardChange.emit(dashboard);
          this.chartService.removeChart(chartId).pipe(takeUntil(this.destroy$)).subscribe();
          this.pop.toast(this.translate.instant('editor.successfully_removed_chart'), 'success', 4000, 'delete');
        },
        error: () => this.pop.error(this.translate.instant('units.error_removing_unit'))
      });
    });
  }

  private updateDashboardState(updatedDashboard: Dashboard): void {
    this.dashboard = updatedDashboard;
    this.items = this.dashboard.charts as WidgetV2[];
    this.refreshLayout();
    if (!this.scope) this.dashboardStore.updateDashboard(updatedDashboard);
  }

  failedNames(failed: { name: string; project: string }[]): string {
    return failed.map(source => source.name || source.project).join(', ');
  }

  showEdit(item: WidgetV2) {
    this.items = this.dashboard.charts as WidgetV2[];
    this.items.map(i => i.showEdit = false);
    item.showEdit = true;
    this.pointerItem = item;
  }

  // The mouse left the widget: its buttons go, unless its menu is open (they go when it closes).
  hideEdit(item: WidgetV2) {
    if (this.pointerItem === item) this.pointerItem = null;
    if (this.menuItem !== item) item.showEdit = false;
  }

  closeMenu(item: WidgetV2) {
    this.menuItem = null;
    if (this.pointerItem !== item) item.showEdit = false;
  }

  // Charts are created by the widget host, out of reach of a ViewChildren query.
  private chartOf(item: WidgetV2): ChartComponent | undefined {
    return this.widgetHosts?.map(host => host.instance)
      .find((instance): instance is ChartComponent => instance instanceof ChartComponent && instance.widget?.id === item.id);
  }

  isChartZoomed(item: WidgetV2): boolean {
    if (item.type !== 'chart') return false;

    const chartComponent = this.chartOf(item);

    if (chartComponent && chartComponent.isZoomed) {
      return chartComponent.isZoomed();
    }

    return false;
  }

  zoomBackChart(item: WidgetV2): void {
    const chartComponent = this.chartOf(item);

    if (chartComponent && typeof chartComponent.zoomBack === 'function') {
      chartComponent.zoomBack();
    }
  }

  resetZoomChart(item: WidgetV2): void {
    const chartComponent = this.chartOf(item);

    if (chartComponent && typeof chartComponent.resetZoom === 'function') {
      chartComponent.resetZoom();
    }
  }
}